import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Generic mutation hook.
 * Eliminates the repeated `setIsSubmitting + try/catch/finally + setError + setBanner`
 * pattern across form and action handlers.
 *
 * @template TInput, TResult
 * @param {(input: TInput) => Promise<TResult>} mutator - Async function performing the side effect
 *   (typically an apiRequest call). Receives whatever the caller passes to `mutate`.
 * @param {object} [options]
 * @param {(result: TResult, input: TInput) => void} [options.onSuccess] - Called after the
 *   mutator resolves, before state updates are flushed.
 * @param {(error: Error, input: TInput) => void} [options.onError] - Called when the mutator
 *   rejects. If you set state in here, it will be applied after the hook's own error state.
 * @param {string|((result: TResult, input: TInput) => string)} [options.successMessage] -
 *   Optional success banner copy. Can be a string or a function of (result, input).
 * @param {string} [options.errorFallback='Unable to complete the request. Try again.'] -
 *   Fallback error copy when the thrown error has no message.
 * @returns {{
 *   mutate: (input: TInput) => Promise<TResult | undefined>,
 *   isLoading: boolean,
 *   error: string,
 *   successMessage: string,
 *   data: TResult | null,
 *   reset: () => void
 * }}
 *   `mutate` resolves with the result on success, or `undefined` on failure (the error is
 *   surfaced via the `error` state). It never throws — callers drive UX from hook state.
 */
export function useMutation(mutator, options = {}) {
  const {
    onSuccess,
    onError,
    successMessage,
    errorFallback = 'Unable to complete the request. Try again.'
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState('');
  const [data, setData] = useState(null);

  // Keep the latest callbacks in refs so changing them between renders does not
  // force `mutate` to re-identify and re-trigger downstream effects.
  const mutatorRef = useRef(mutator);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const successMessageRef = useRef(successMessage);
  const mountedRef = useRef(true);

  useEffect(() => {
    mutatorRef.current = mutator;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    successMessageRef.current = successMessage;
  });

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const reset = useCallback(() => {
    if (!mountedRef.current) return;
    setError('');
    setBanner('');
    setData(null);
    setIsLoading(false);
  }, []);

  const mutate = useCallback(async (input) => {
    if (mountedRef.current) {
      setIsLoading(true);
      setError('');
      setBanner('');
    }

    try {
      const result = await mutatorRef.current(input);
      if (!mountedRef.current) return result;

      setData(result);

      const resolved = typeof successMessageRef.current === 'function'
        ? successMessageRef.current(result, input)
        : successMessageRef.current;
      if (resolved) {
        setBanner(resolved);
      }

      if (onSuccessRef.current) {
        onSuccessRef.current(result, input);
      }

      return result;
    } catch (err) {
      if (!mountedRef.current) return undefined;

      setError(err?.message || errorFallback);

      if (onErrorRef.current) {
        onErrorRef.current(err, input);
      }

      return undefined;
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [errorFallback]);

  return {
    mutate,
    isLoading,
    error,
    successMessage: banner,
    data,
    reset
  };
}
